<script setup lang="ts">
import { NEmpty, NSpin } from 'naive-ui'
import QueueErrorState from './QueueErrorState.vue'
import type { AvailableModel } from '../stores/usage'
import { formatContext, formatRate, modelColor, modelName } from '../composables/usageFormatting'

// The "Available models" half of UsageView.vue (#1090) — the live
// Anthropic model catalog with pricing. Extracted so the parent stays
// under the 300-line hard limit; no behavior change from the inline
// version. Rendered independently of the weekly-spend section — its
// own load/error/empty states, its own ~24h-cached endpoint — so
// neither view can blank the other.
defineProps<{
  loading: boolean
  error: string | null
  models: AvailableModel[]
  stale: boolean
}>()

const emit = defineEmits<{ retry: [] }>()
</script>

<template>
  <section
    data-testid="available-models"
    aria-labelledby="usage-models-heading"
    class="flex flex-col gap-3 rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
  >
    <header class="flex flex-col gap-1">
      <h2 id="usage-models-heading" class="m-0 text-[15px] font-semibold text-ink">Available models</h2>
      <p class="m-0 text-xs text-ink-muted">
        Models your account can use, pulled live from Anthropic and priced per 1M tokens. Models the brain isn’t wired
        to use yet are marked
        <span class="text-ink">available</span> — handy for spotting newer options.
      </p>
    </header>

    <div v-if="loading" class="flex items-center justify-center py-8">
      <NSpin />
    </div>

    <QueueErrorState v-else-if="error" :message="error" @retry="emit('retry')" />

    <NEmpty v-else-if="models.length === 0" description="No models to show." class="my-8" />

    <template v-else>
      <p v-if="stale" data-testid="models-stale" class="m-0 text-xs text-ink-muted">
        Showing last-known data — couldn’t reach Anthropic just now.
      </p>

      <div class="-mx-4 overflow-x-auto px-4 sm:-mx-5 sm:px-5">
        <table class="w-full border-collapse text-sm">
          <caption class="sr-only">
            Available models and per-1M-token pricing. Scroll horizontally to see all columns.
          </caption>
          <thead>
            <tr class="text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
              <th class="py-2 pr-3 font-semibold">Model</th>
              <th class="px-2 py-2 font-semibold">Status</th>
              <th class="px-2 py-2 text-right font-semibold">Context</th>
              <th class="px-2 py-2 text-right font-semibold">Input</th>
              <th class="px-2 py-2 text-right font-semibold">Output</th>
              <th class="hidden px-2 py-2 text-right font-semibold sm:table-cell">
                <abbr title="Cache read" class="no-underline">Cache R</abbr>
              </th>
              <th class="hidden py-2 pl-2 text-right font-semibold sm:table-cell">
                <abbr title="Cache write" class="no-underline">Cache W</abbr>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in models" :key="m.id" data-testid="model-row" class="border-t border-border-divider">
              <td class="py-2.5 pr-3">
                <div class="flex items-center gap-2.5">
                  <span class="inline-block size-2 shrink-0 rounded-sm" :class="modelColor(m.id)" aria-hidden="true" />
                  <div class="flex flex-col leading-tight">
                    <span class="font-medium text-ink">{{ modelName(m) }}</span>
                    <span class="font-mono text-xs text-ink-faint">{{ m.id }}</span>
                    <!-- Below sm the Cache R/W columns are hidden to keep the
                         table from overflowing; surface them here instead. -->
                    <span class="mt-0.5 text-xs text-ink-faint tabular-nums sm:hidden" data-testid="model-cache-mobile">
                      Cache read {{ formatRate(m.pricing?.cache_read_cents) }} · write
                      {{ formatRate(m.pricing?.cache_write_cents) }}
                    </span>
                    <span
                      v-if="m.pricing?.long_context"
                      class="mt-0.5 text-xs text-ink-faint tabular-nums"
                      data-testid="model-long-context"
                    >
                      Prompts over {{ formatContext(m.pricing.long_context.threshold_tokens) }}:
                      {{ formatRate(m.pricing.long_context.input_cents) }} in ·
                      {{ formatRate(m.pricing.long_context.output_cents) }} out
                    </span>
                  </div>
                </div>
              </td>
              <td class="px-2 py-2.5">
                <span
                  v-if="m.in_use"
                  data-testid="model-status"
                  class="inline-block whitespace-nowrap rounded-full bg-brand-primary/15 px-2 py-0.5 text-xs font-medium text-brand-primary"
                  >in use · {{ m.tier }}</span
                >
                <span
                  v-else
                  data-testid="model-status"
                  class="inline-block rounded-full bg-surface-hover px-2 py-0.5 text-xs text-ink-muted"
                  >available</span
                >
              </td>
              <td class="px-2 py-2.5 text-right font-mono text-xs text-ink-muted tabular-nums">
                {{ formatContext(m.context_window) }}
              </td>
              <td class="px-2 py-2.5 text-right font-mono text-xs text-ink tabular-nums">
                {{ formatRate(m.pricing?.input_cents) }}
              </td>
              <td class="px-2 py-2.5 text-right font-mono text-xs text-ink tabular-nums">
                {{ formatRate(m.pricing?.output_cents) }}
              </td>
              <td class="hidden px-2 py-2.5 text-right font-mono text-xs text-ink-muted tabular-nums sm:table-cell">
                {{ formatRate(m.pricing?.cache_read_cents) }}
              </td>
              <td class="hidden py-2.5 pl-2 text-right font-mono text-xs text-ink-muted tabular-nums sm:table-cell">
                {{ formatRate(m.pricing?.cache_write_cents) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="m-0 text-xs text-ink-faint">
        Prices are per 1M tokens. “—” means there’s no maintained price for that model yet.
      </p>
    </template>
  </section>
</template>
